import type { DiscountAttributes as GQLInput } from '../../../generated/api.graphql';
import { take } from '../../../util';
import { DiscountKind } from './DiscountKind';

/**
 * What a caller hands `toGqlInput` to create or update a discount. The legacy `amount` string is
 * deliberately absent: the structured `numericAmount` + `kind` pair is what the SPA sends, since
 * that string's `amount =~ /%/` parse is the guess the pair replaced. `eventId` is the one
 * requirement, and `limit`/`unlimited` and `expiration`/`noExpiration` are pairs whose precedence
 * the backend owns, so a form sets whichever half it means.
 */
export type DiscountInput = {
  id?: number | null;
  eventId: number;
  code?: string | null;
  kind?: DiscountKind | null;
  numericAmount?: number | null;
  limit?: number | null;
  unlimited?: boolean | null;
  expiration?: string | null;
  noExpiration?: boolean | null;
  joinitMembershipType?: string | null;
  ticketBlueprintIds?: number[] | null;
};

/**
 * Every field the mutation accepts. A misspelling is a compile error — `take` checks the keys
 * against `DiscountInput` — but an omission is not, so the round-trip test in
 * `DiscountInput.spec.ts` is what catches a field this list forgets.
 */
const GQL_FIELDS = [
  'code', 'eventId', 'expiration', 'id', 'joinitMembershipType', 'kind', 'limit',
  'noExpiration', 'numericAmount', 'ticketBlueprintIds', 'unlimited',
] as const;

/**
 * Whitelists a caller's values down to what the mutation accepts: a form seeded from a discount
 * read back carries the read-only `label`, `type`, `amount`, `timesUsed`, `usable` and the two
 * timestamps, and the mutation rejects unknown arguments outright. `take()` drops undefined keys,
 * so a deliberate `id: undefined` still creates rather than updates.
 */
export const toGqlInput = (input: DiscountInput): GQLInput =>
  take<GQLInput, DiscountInput>(input, ...GQL_FIELDS);
